/**
 * Paramètres (/parametres/:rubrique) — menu latéral secondaire par catégories + rubrique active.
 * Le sommelier n'a accès qu'à Plan de cave, Formats et Carte en lecture ; le reste est gérant.
 */
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Etablissement from './rubrics/Etablissement';
import Utilisateurs from './rubrics/Utilisateurs';
import PlanCave from './rubrics/PlanCave';
import Formats from './rubrics/Formats';
import AlertesSeuils from './rubrics/AlertesSeuils';
import ModesVente from './rubrics/ModesVente';
import Fournisseurs from './rubrics/Fournisseurs';
import IASettings from './rubrics/IASettings';
import Tags from './rubrics/Tags';
import Specificites from './rubrics/Specificites';
import Corbeille from './rubrics/Corbeille';
import HorsLigne from './rubrics/HorsLigne';
import Importer from './rubrics/Importer';
import Exporter from './rubrics/Exporter';
import DefautsBouteille from './rubrics/DefautsBouteille';
import ScannerSettings from './rubrics/ScannerSettings';
import Raccourcis from './rubrics/Raccourcis';

// Catégories → rubriques (clé de route, libellé, composant, rôles autorisés).
const NAV = [
  { cat: 'Établissement', items: [
    { key: 'etablissement', label: 'Établissement', comp: Etablissement, roles: ['gerant'] },
    { key: 'utilisateurs', label: 'Utilisateurs et rôles', comp: Utilisateurs, roles: ['gerant', 'sommelier', 'serveur'] },
    { key: 'hors-ligne', label: 'Hors-ligne et synchro', comp: HorsLigne, roles: ['gerant'] },
    { key: 'raccourcis', label: 'Raccourcis clavier', comp: Raccourcis, roles: ['gerant', 'sommelier'] },
  ] },
  { cat: 'Cave', items: [
    { key: 'plan-de-cave', label: 'Plan de cave', comp: PlanCave, roles: ['gerant', 'sommelier'] },
    { key: 'formats', label: 'Formats de bouteille', comp: Formats, roles: ['gerant', 'sommelier'] },
    { key: 'defauts-bouteille', label: 'Cuvée-Bouteille — défauts', comp: DefautsBouteille, roles: ['gerant'] },
    { key: 'scanner', label: 'Scanner entrée-sortie', comp: ScannerSettings, roles: ['gerant', 'sommelier'] },
    { key: 'alertes', label: 'Alertes et seuils', comp: AlertesSeuils, roles: ['gerant'] },
    { key: 'tags', label: 'Tags', comp: Tags, roles: ['gerant', 'sommelier'] },
    { key: 'specificites', label: 'Spécificités', comp: Specificites, roles: ['gerant', 'sommelier'] },
    { key: 'corbeille', label: 'Corbeille', comp: Corbeille, roles: ['gerant', 'sommelier'] },
  ] },
  { cat: 'Ventes et achats', items: [
    { key: 'modes-de-vente', label: 'Modes de vente et coef.', comp: ModesVente, roles: ['gerant'] },
    { key: 'fournisseurs', label: 'Fournisseurs', comp: Fournisseurs, roles: ['gerant', 'sommelier'] },
  ] },
  { cat: 'Carte et IA', items: [
    { key: 'carte', label: 'Carte des vins · éditions', comp: null, roles: ['gerant', 'sommelier'], to: '/carte/editions' },
    { key: 'ia', label: 'IA · OCR et traduction', comp: IASettings, roles: ['gerant'] },
  ] },
  { cat: 'Données', items: [
    { key: 'importer', label: 'Importer', comp: Importer, roles: ['gerant'] },
    { key: 'exporter', label: 'Exporter', comp: Exporter, roles: ['gerant', 'sommelier'] },
  ] },
];

const ALL = NAV.flatMap((c) => c.items);

export default function Parametres() {
  const { rubrique } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role;

  const current = ALL.find((i) => i.key === rubrique);
  if (!current) return <Navigate to="/parametres/etablissement" replace />;
  if (current.to) { navigate(current.to, { replace: true }); return null; }
  const allowed = current.roles.includes(role);
  const Comp = current.comp;

  return (
    <div className="flex min-h-[calc(100vh-56px)]">
      {/* Menu latéral secondaire */}
      <nav className="w-[240px] shrink-0 hair-r p-4 bg-surface/30" data-testid="parametres-nav">
        <h1 className="font-title text-2xl mb-4">Paramètres</h1>
        {NAV.map((c) => (
          <div key={c.cat} className="mb-4">
            <p className="kicker text-gold700 mb-1.5">{c.cat}</p>
            {c.items.filter((i) => i.roles.includes(role)).map((i) => (
              <button key={i.key} onClick={() => navigate(i.to ? i.to : `/parametres/${i.key}`)}
                data-testid={`nav-${i.key}`}
                className={`w-full text-left px-3 py-1.5 rounded text-[13.5px] transition-colors relative ${rubrique === i.key ? 'bg-gold100 text-gold800 font-medium' : 'text-n700 hover:bg-surface'}`}>
                {rubrique === i.key && <span className="absolute left-0 top-1 bottom-1 w-0.5 bg-gold rounded-full" />}
                {i.label}
              </button>
            ))}
          </div>
        ))}
      </nav>
      <div className="flex-1 min-w-0 p-4 md:p-8" data-testid="parametres-content">
        {allowed ? <Comp readOnly={role === 'sommelier'} /> : (
          <p className="text-sm text-n600">Accès réservé au gérant.</p>
        )}
      </div>
    </div>
  );
}
